import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { InventoryReceipt, RequestedStop, ActualStop, InventoryReceiptType } from '@ironsight/types'

// TODO: Move all bottom sheets state from Ticket.vue to here
export const useJobBottomSheetStatesStore = defineStore('jobBottomSheetStates', () => {
  interface StopConfirmationBottomSheetState {
    isVisible: boolean
    requestedStop: RequestedStop | null
    actualStop: ActualStop | null
    isSkipping?: boolean
    receiptType: InventoryReceiptType | null
  }

  interface ReceiptDetailsBottomSheetState {
    isVisible: boolean
    receipts: InventoryReceipt[] | null
    actualStop: ActualStop | null
    receiptType: InventoryReceiptType | null
  }

  interface ActualSkippedStopDetailsBottomSheetState {
    isVisible: boolean
    actualStop: ActualStop | null
  }

  interface FormSubmissionBottomSheetState {
    isVisible: boolean
    isEditing: boolean
    formUuid: string | null
    formSubmissionUuid: string | null
    stopId: string | null
  }

  interface BottomSheetState {
    isTagBottomSheetVisible: boolean
    isNewGuestUserBottomSheetVisible: boolean
    isSignatureBottomSheetVisible: boolean
    isLineItemBottomSheetVisible: boolean
    isBatchBottomSheetVisible: boolean
    isProjectBottomSheetVisible: boolean
    isSendingEmailForApprovalBottomSheetVisible: boolean
    isRejectingApprovalBottomSheetVisible: boolean
    isEditingPlannedActualTimesBottomSheetVisible: boolean
    isEditingJobDetailsBottomSheetVisible: boolean
    isCreatingLocationOnTheFlyBottomSheetVisible: boolean
    requestedStopFormBottomSheetVisible: boolean
    receiptDetailsBottomSheetStates: ReceiptDetailsBottomSheetState
    actualSkippedStopDetailsBottomSheetStates: ActualSkippedStopDetailsBottomSheetState
    stopConfirmationBottomSheetStates: StopConfirmationBottomSheetState
    formSubmissionBottomSheetState: FormSubmissionBottomSheetState
    isSelectingFormBottomSheetVisible: boolean
  }

  const emptyState: BottomSheetState = {
    isTagBottomSheetVisible: false,
    isNewGuestUserBottomSheetVisible: false,
    isSignatureBottomSheetVisible: false,
    isLineItemBottomSheetVisible: false,
    isBatchBottomSheetVisible: false,
    isProjectBottomSheetVisible: false,
    isSendingEmailForApprovalBottomSheetVisible: false,
    isRejectingApprovalBottomSheetVisible: false,
    isEditingPlannedActualTimesBottomSheetVisible: false,
    isEditingJobDetailsBottomSheetVisible: false,
    isCreatingLocationOnTheFlyBottomSheetVisible: false,
    requestedStopFormBottomSheetVisible: false,
    receiptDetailsBottomSheetStates: {
      isVisible: false,
      receipts: null,
      actualStop: null,
      receiptType: null,
    },
    actualSkippedStopDetailsBottomSheetStates: {
      isVisible: false,
      actualStop: null,
    },
    stopConfirmationBottomSheetStates: {
      isVisible: false,
      requestedStop: null,
      actualStop: null,
      isSkipping: false,
      receiptType: null,
    },
    formSubmissionBottomSheetState: {
      isVisible: false,
      isEditing: false,
      formUuid: null,
      formSubmissionUuid: null,
      stopId: null,
    },
    isSelectingFormBottomSheetVisible: false,
  }

  const jobBottomSheetStates = ref<BottomSheetState>(emptyState)

  const isBottomSheetVisible = computed(
    () =>
      jobBottomSheetStates.value.isTagBottomSheetVisible ||
      jobBottomSheetStates.value.isNewGuestUserBottomSheetVisible ||
      jobBottomSheetStates.value.isSignatureBottomSheetVisible ||
      jobBottomSheetStates.value.isLineItemBottomSheetVisible ||
      jobBottomSheetStates.value.isBatchBottomSheetVisible ||
      jobBottomSheetStates.value.isProjectBottomSheetVisible ||
      jobBottomSheetStates.value.isCreatingLocationOnTheFlyBottomSheetVisible,
  )

  const isCompletedJobBottomSheetVisible = computed(
    () =>
      jobBottomSheetStates.value.isEditingJobDetailsBottomSheetVisible ||
      jobBottomSheetStates.value.isTagBottomSheetVisible ||
      jobBottomSheetStates.value.isEditingPlannedActualTimesBottomSheetVisible ||
      jobBottomSheetStates.value.isLineItemBottomSheetVisible ||
      jobBottomSheetStates.value.isBatchBottomSheetVisible ||
      jobBottomSheetStates.value.isRejectingApprovalBottomSheetVisible ||
      jobBottomSheetStates.value.isSendingEmailForApprovalBottomSheetVisible ||
      jobBottomSheetStates.value.isSignatureBottomSheetVisible ||
      jobBottomSheetStates.value.receiptDetailsBottomSheetStates.isVisible ||
      jobBottomSheetStates.value.actualSkippedStopDetailsBottomSheetStates.isVisible ||
      jobBottomSheetStates.value.stopConfirmationBottomSheetStates.isVisible ||
      jobBottomSheetStates.value.requestedStopFormBottomSheetVisible ||
      jobBottomSheetStates.value.isProjectBottomSheetVisible ||
      jobBottomSheetStates.value.formSubmissionBottomSheetState.isVisible ||
      jobBottomSheetStates.value.isSelectingFormBottomSheetVisible,
  )

  const isBottomSheetFullHeight = computed(
    () =>
      !jobBottomSheetStates.value.isBatchBottomSheetVisible &&
      !jobBottomSheetStates.value.isNewGuestUserBottomSheetVisible &&
      !jobBottomSheetStates.value.isTagBottomSheetVisible,
  )

  const isCompletedJobBottomSheetFullHeight = computed(
    () =>
      jobBottomSheetStates.value.isLineItemBottomSheetVisible ||
      jobBottomSheetStates.value.isRejectingApprovalBottomSheetVisible ||
      jobBottomSheetStates.value.isSendingEmailForApprovalBottomSheetVisible ||
      jobBottomSheetStates.value.isCreatingLocationOnTheFlyBottomSheetVisible ||
      jobBottomSheetStates.value.isProjectBottomSheetVisible ||
      jobBottomSheetStates.value.formSubmissionBottomSheetState.isVisible ||
      jobBottomSheetStates.value.isSelectingFormBottomSheetVisible,
  )

  const setJobBottomSheetStates = (newState: {
    isTagBottomSheetVisible?: boolean
    isNewGuestUserBottomSheetVisible?: boolean
    isSignatureBottomSheetVisible?: boolean
    isLineItemBottomSheetVisible?: boolean
    isBatchBottomSheetVisible?: boolean
    isProjectBottomSheetVisible?: boolean
    isSendingEmailForApprovalBottomSheetVisible?: boolean
    isRejectingApprovalBottomSheetVisible?: boolean
    isEditingPlannedActualTimesBottomSheetVisible?: boolean
    isEditingJobDetailsBottomSheetVisible?: boolean
    isCreatingLocationOnTheFlyBottomSheetVisible?: boolean
    requestedStopFormBottomSheetVisible?: boolean
    receiptDetailsBottomSheetStates?: ReceiptDetailsBottomSheetState
    actualSkippedStopDetailsBottomSheetStates?: ActualSkippedStopDetailsBottomSheetState
    stopConfirmationBottomSheetStates?: StopConfirmationBottomSheetState
    formSubmissionBottomSheetState?: FormSubmissionBottomSheetState
    isSelectingFormBottomSheetVisible?: boolean
  }) => {
    jobBottomSheetStates.value = {
      ...jobBottomSheetStates.value,
      ...newState,
    }
  }

  const clearJobBottomSheetStates = () => {
    jobBottomSheetStates.value = emptyState
  }

  return {
    jobBottomSheetStates,
    isBottomSheetVisible,
    isCompletedJobBottomSheetVisible,
    isBottomSheetFullHeight,
    isCompletedJobBottomSheetFullHeight,
    setJobBottomSheetStates,
    clearJobBottomSheetStates,
  }
})
